import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Row, Col, Card, Spinner } from 'react-bootstrap';
import { Calendar, Add, Trash } from 'iconsax-react';
import Select from 'react-select';
import MyApiClient from '../../../../api-client/MyApiClient';
import { sports } from '../../Util/enum';
import { sidSelectStyles } from '../../Util/sidSelectStyles';
import chiediConferma from '../../Util/chiediConferma';
import { conMotivo } from '../../Util/erroreServer';
import { segnalaMancanti } from '../../Util/campiMancanti';
import {
    isoGiorno, seduteScoperte, seduteInFascia, descriviSeduta, chiaveGruppo, prodottoDaDettaglio, TIPI_PRODOTTO,
} from '../../Impianti/disponibilitaImpianto';

const dayOptions = [
    { value: 'MONDAY', label: 'Lunedì' },
    { value: 'TUESDAY', label: 'Martedì' },
    { value: 'WEDNESDAY', label: 'Mercoledì' },
    { value: 'THURSDAY', label: 'Giovedì' },
    { value: 'FRIDAY', label: 'Venerdì' },
    { value: 'SATURDAY', label: 'Sabato' },
    { value: 'SUNDAY', label: 'Domenica' }
];

const statusLabels = {
    DRAFT: { label: 'Bozza', cls: 'text-warning' },
    ACTIVE: { label: 'Attiva', cls: 'text-success' },
    CANCELLED: { label: 'Annullata', cls: 'text-danger' }
};

// Menu della tendina fuori dalla tabella (che ha overflow)
const selectStyles = { ...sidSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 2000 }) };

const dataIt = (s) => {
    const [y, m, d] = (s || '').slice(0, 10).split('-');
    return y && m && d ? `${d}/${m}/${y}` : s || '';
};

const FORM_VUOTO = { sport: '', startDate: '', endDate: '', scheduleItems: [] };

// Lunedì-domenica con l'orario predefinito (nuova disponibilità), come per gli impianti
const settimanaPredefinita = () => dayOptions.map((day, index) => ({
    id: (Date.now() + index).toString(),
    dayOfWeek: day.value,
    startTime: '08:00',
    endTime: '20:00',
    location: ''
}));

const token = () => {
    try { return JSON.parse(sessionStorage.getItem('userDetails')).idToken; } catch { return null; }
};

/*
 * Disponibilità del maestro. Niente finestre: il tasto «Nuova disponibilità» (uno solo, al centro) apre il box
 * nella pagina, e così fa un clic su una riga. Tasti del box (set-ui, tasti-fine-scheda): «Rimuovi» a sinistra,
 * «Indietro» e «Salva» a destra; «Salva» c'è solo quando la disponibilità si può ancora modificare (bozza o nuova).
 */
const DisponibilitaMaestro = ({ profile, readonly = false }) => {
    const navigate = useNavigate();
    const [disponibilita, setDisponibilita] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nuova, setNuova] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [enabledSports, setEnabledSports] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState(FORM_VUOTO);
    // Sedute future prenotate dal maestro (corsi e lezioni), nei periodi delle disponibilità non finite
    const [prenotate, setPrenotate] = useState({ caricate: false, lista: [], errore: '' });
    // Prodotti collegati risolti per gruppo di sedute (cache)
    const [risolti, setRisolti] = useState({});
    const richiestiRef = useRef(new Set());
    const corsiRef = useRef(null);
    const guruId = profile?.idsGuru?.userId;

    const fetchDisponibilita = useCallback(async () => {
        try {
            setLoading(true);
            const t = token();
            if (t) {
                const response = await new MyApiClient().getGuruSchedules(t, profile.idsGuru.userId);
                if (response.error) {
                    console.error('Errore API:', response.error);
                    setDisponibilita([]);
                } else {
                    setDisponibilita(response || []);
                }
            }
        } catch (error) {
            console.error('Errore nel caricamento delle disponibilità:', error);
        } finally {
            setLoading(false);
        }
    }, [profile]);

    useEffect(() => {
        if (profile && profile.idsGuru && profile.idsGuru.userId) {
            fetchDisponibilita();
        }
    }, [profile, fetchDisponibilita]);

    useEffect(() => {
        const fetchEnabledSportsForGuru = async () => {
            try {
                const t = token();
                if (t && profile && profile.idsGuru && profile.idsGuru.userId) {
                    const memberships = await new MyApiClient().getMemberships(t, 'COACH', null, 'CONFIRMED');
                    const guruId = profile.idsGuru.userId;
                    const approvedSportsValues = Array.isArray(memberships)
                        ? memberships
                            .filter(m => m.userId === guruId && m.membershipRank === 'COACH' && m.status === 'CONFIRMED' && m.sport)
                            .map(m => m.sport)
                        : [];
                    setEnabledSports(sports.filter(s => approvedSportsValues.includes(s.value)));
                }
            } catch (error) {
                console.error('Errore nel caricamento degli sport approvati:', error);
                setEnabledSports([]);
            }
        };
        fetchEnabledSportsForGuru();
    }, [profile]);

    const oggi = isoGiorno(new Date());

    // Righe il cui periodo non è finito: una sola chiamata al calendario sull'unione dei periodi
    const righeDaVerificare = useMemo(() => disponibilita.filter((d) =>
        d.status !== 'CANCELLED' && d.startDate && d.endDate && d.endDate.slice(0, 10) >= oggi), [disponibilita, oggi]);

    useEffect(() => {
        if (!guruId || righeDaVerificare.length === 0) {
            setPrenotate({ caricate: true, lista: [], errore: '' });
            return undefined;
        }
        let annullato = false;
        const da = righeDaVerificare.reduce((min, d) => {
            const inizio = d.startDate.slice(0, 10) > oggi ? d.startDate.slice(0, 10) : oggi;
            return inizio < min ? inizio : min;
        }, '9999-12-31');
        const a = righeDaVerificare.reduce((max, d) => (d.endDate.slice(0, 10) > max ? d.endDate.slice(0, 10) : max), oggi);
        setPrenotate((p) => ({ ...p, caricate: false }));
        (async () => {
            try {
                const res = await new MyApiClient().getCalendarioEventi(token(), `${da}T00:00:00`, `${a}T23:59:59`, { maestri: guruId });
                if (annullato) return;
                const adesso = new Date();
                if (res?.error) {
                    setPrenotate({ caricate: true, lista: [], errore: res.error });
                    return;
                }
                const lista = (res?.eventi || [])
                    .filter((e) => e.dataInizio && e.dataFine && new Date(e.dataFine) > adesso)
                    .sort((x, y) => new Date(x.dataInizio) - new Date(y.dataInizio));
                setPrenotate({ caricate: true, lista, errore: '' });
            } catch (e) {
                if (!annullato) setPrenotate({ caricate: true, lista: [], errore: 'Verifica sedute non disponibile' });
            }
        })();
        return () => { annullato = true; };
    }, [righeDaVerificare, guruId, oggi]);

    // Sedute future di ogni riga: dal giorno più tardo tra oggi e l'inizio, fino alla fine del periodo
    const impegni = useMemo(() => {
        const mappa = {};
        righeDaVerificare.forEach((d) => {
            const inizio = d.startDate.slice(0, 10) > oggi ? d.startDate.slice(0, 10) : oggi;
            const fine = d.endDate.slice(0, 10);
            const eventi = prenotate.lista.filter((e) => {
                const giorno = isoGiorno(new Date(e.dataInizio));
                return giorno >= inizio && giorno <= fine;
            });
            if (eventi.length) mappa[d.id] = eventi;
        });
        return mappa;
    }, [righeDaVerificare, prenotate.lista, oggi]);

    // La verifica serve per una disponibilità attiva il cui periodo arriva a oggi o oltre (le sedute si prenotano solo sulle attive)
    const verificaAttiva = !!editingItem && editingItem.status === 'ACTIVE' && !!editingItem.endDate && editingItem.endDate.slice(0, 10) >= oggi;
    const sedute = useMemo(() => ({
        inCorso: verificaAttiva && !prenotate.caricate,
        lista: verificaAttiva ? impegni[editingItem.id] || [] : [],
        errore: verificaAttiva ? prenotate.errore : '',
    }), [verificaAttiva, prenotate.caricate, prenotate.errore, impegni, editingItem]);

    // Sedute che cadono in ciascun orario originale: gli orari con sedute sono impegnati e non si toccano
    const seduteFascia = useCallback((f) => {
        if (!f?._orig) return [];
        const { dayOfWeek, startTime, endTime } = f._orig;
        return sedute.lista.filter((s) => seduteInFascia(s, dayOfWeek, startTime, endTime));
    }, [sedute.lista]);

    const eventiBloccati = useMemo(() => {
        const visti = new Set();
        const out = [];
        formData.scheduleItems.forEach((f) => seduteFascia(f).forEach((e) => {
            if (!visti.has(e.id)) {
                visti.add(e.id);
                out.push(e);
            }
        }));
        return out;
    }, [formData.scheduleItems, seduteFascia]);

    // Risolve (una volta sola, poi in cache) il prodotto di ciascun gruppo di sedute degli orari impegnati, per il collegamento diretto
    useEffect(() => {
        const daRisolvere = [];
        eventiBloccati.forEach((e) => {
            const k = chiaveGruppo(e);
            if (!richiestiRef.current.has(k)) {
                richiestiRef.current.add(k);
                daRisolvere.push({ k, e });
            }
        });
        if (daRisolvere.length === 0) return;
        const t = token();
        const api = new MyApiClient();
        (async () => {
            if (!corsiRef.current && daRisolvere.some(({ e }) => e.type === 'corso')) {
                corsiRef.current = api.getBookingCourses(t).then((r) => (Array.isArray(r) ? r : [])).catch(() => []);
            }
            const corsi = corsiRef.current ? await corsiRef.current : [];
            await Promise.all(daRisolvere.map(async ({ k, e }) => {
                let d = null;
                if (e.type !== 'generico') {
                    try {
                        const res = await api.getCalendarioEventoById(t, e.id);
                        d = res?.error ? null : res;
                    } catch (err) {
                        d = null;
                    }
                }
                setRisolti((r) => ({ ...r, [k]: prodottoDaDettaglio(e, d, corsi) }));
            }));
        })();
    }, [eventiBloccati]);

    // Sedute prenotate che con le date e gli orari del modulo resterebbero fuori dalla disponibilità
    const scoperte = useMemo(() => seduteScoperte(sedute.lista, {
        startDate: formData.startDate,
        endDate: formData.endDate,
        openingHoursItems: formData.scheduleItems.map((f) => ({ dayOfWeek: f.dayOfWeek, openTime: f.startTime, closeTime: f.endTime })),
    }), [sedute.lista, formData]);

    const apriNuova = () => {
        setErrorMessage('');
        setEditingItem(null);
        // Settimana intera già compilata, come nella disponibilità degli impianti
        setFormData({ ...FORM_VUOTO, scheduleItems: settimanaPredefinita() });
        setNuova(true);
    };

    // Riga cliccata: apre il box sotto la riga (o lo chiude se era già aperto)
    const apriPannello = (item) => {
        if (readonly) return;
        setErrorMessage('');
        setNuova(false);
        if (editingItem?.id === item.id) {
            setEditingItem(null);
            return;
        }
        setEditingItem(item);
        setFormData({
            sport: item.sport,
            startDate: item.startDate ? item.startDate.slice(0, 10) : '',
            endDate: item.endDate ? item.endDate.slice(0, 10) : '',
            scheduleItems: [...(item.scheduleItems || [])].sort((a, b) =>
                dayOptions.findIndex(d => d.value === a.dayOfWeek) - dayOptions.findIndex(d => d.value === b.dayOfWeek)
                || (a.startTime || '').localeCompare(b.startTime || '')).map((s, i) => ({
                ...s,
                id: s.id || `${Date.now()}-${i}`,
                // Valori salvati: decidono se l'orario è impegnato da sedute prenotate
                _orig: { dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime },
            }))
        });
    };

    const chiudi = () => {
        setErrorMessage('');
        setNuova(false);
        setEditingItem(null);
        setFormData(FORM_VUOTO);
    };

    // Nuova, in bozza e attiva si modificano (come per gli impianti); l'annullata no
    const modificabile = (item) => !item || item.status !== 'CANCELLED';

    // Dati mancanti: niente avviso a parte, si segnalano i campi nel box.
    // `conOrari`: per «Salva e attiva» servono anche gli orari settimanali completi.
    const controllaDati = (conOrari) => {
        const mancanti = [];
        if (!formData.sport) mancanti.push({ campo: 'disp-sport', messaggio: 'Manca lo sport' });
        if (!formData.startDate) mancanti.push({ campo: 'disp-data-inizio', messaggio: 'Manca la data di inizio' });
        if (!formData.endDate) mancanti.push({ campo: 'disp-data-fine', messaggio: 'Manca la data di fine' });
        if (conOrari) {
            const orari = formData.scheduleItems || [];
            if (orari.length === 0) {
                mancanti.push({ campo: 'disp-aggiungi-orario', messaggio: 'Aggiungi almeno un orario settimanale' });
            }
            orari.forEach((f, i) => {
                if (!f.dayOfWeek) mancanti.push({ campo: `disp-giorno-${i}`, messaggio: 'Manca il giorno' });
                if (!f.startTime) mancanti.push({ campo: `disp-ora-inizio-${i}`, messaggio: "Manca l'ora di inizio" });
                if (!f.endTime) mancanti.push({ campo: `disp-ora-fine-${i}`, messaggio: "Manca l'ora di fine" });
                else if (f.startTime && f.endTime <= f.startTime) mancanti.push({ campo: `disp-ora-fine-${i}`, messaggio: "L'ora di fine deve essere dopo l'inizio" });
            });
        }
        if (mancanti.length === 0) return true;
        setErrorMessage('');
        // Se nessun campo è raggiungibile (es. sport ancora in caricamento) si ripiega sul messaggio nel box
        if (segnalaMancanti(mancanti) === 0) setErrorMessage(mancanti[0].messaggio);
        return false;
    };

    // Salva la disponibilità (nuova o bozza); con `attiva` la attiva subito dopo. Ritorna true se è andata a buon fine.
    const salva = async (attiva) => {
        if (!controllaDati(attiva)) return;
        try {
            setLoading(true);
            const t = token();
            if (!t) return;
            const apiClient = new MyApiClient();
            const dati = {
                // Una disponibilità attiva resta attiva: lo stato si manda solo per le bozze e le nuove
                status: editingItem?.status === 'ACTIVE' ? undefined : 'DRAFT',
                sport: formData.sport,
                startDate: formData.startDate,
                endDate: formData.endDate,
                scheduleItems: formData.scheduleItems.map(item => ({
                    dayOfWeek: item.dayOfWeek,
                    startTime: item.startTime,
                    endTime: item.endTime,
                    location: item.location
                }))
            };
            let risposta;
            let scheduleId;
            if (editingItem) {
                risposta = await apiClient.updateGuruSchedule(t, editingItem.id, dati);
                scheduleId = editingItem.id;
            } else {
                risposta = await apiClient.createGuruSchedule(t, { guruId: profile.idsGuru.userId, ...dati });
                scheduleId = risposta?.id;
            }
            if (risposta?.error) {
                setErrorMessage('Errore durante il salvataggio: ' + risposta.error);
                return;
            }
            if (attiva) {
                if (!scheduleId) {
                    setErrorMessage('La disponibilità è stata salvata come bozza ma non si è potuta attivare. Riaprila e premi di nuovo «Salva e attiva».');
                    await fetchDisponibilita();
                    return;
                }
                const attivazione = await apiClient.activateGuruSchedule(t, scheduleId);
                if (attivazione?.error) {
                    setErrorMessage('Salvataggio completato, ma errore durante l\'attivazione: ' + attivazione.error);
                    await fetchDisponibilita();
                    return;
                }
            }
            chiudi();
            await fetchDisponibilita();
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
            setErrorMessage(conMotivo('Non è stato possibile salvare la disponibilità. Riprova tra poco.'));
        } finally {
            setLoading(false);
        }
    };

    // «Rimuovi»: la bozza si elimina, l'attiva si cancella (resta annullata). Prima si chiede conferma.
    const rimuovi = async (item) => {
        const bozza = item.status === 'DRAFT';
        const ok = await chiediConferma({
            titolo: 'Eliminare la disponibilità?',
            conferma: 'Elimina',
            testo: bozza
                ? 'La disponibilità viene eliminata. L\'operazione non può essere annullata.'
                : 'La disponibilità attiva viene eliminata. L\'operazione non può essere annullata.',
        });
        if (!ok) return;
        try {
            setLoading(true);
            const t = token();
            if (!t) return;
            const apiClient = new MyApiClient();
            const risposta = bozza
                ? await apiClient.deleteGuruSchedule(t, item.id)
                : await apiClient.cancelGuruSchedule(t, item.id);
            if (risposta?.error) {
                setErrorMessage('Errore durante la rimozione della disponibilità: ' + risposta.error);
                return;
            }
            chiudi();
            await fetchDisponibilita();
        } catch (error) {
            console.error('Errore nella rimozione:', error);
            setErrorMessage('Errore durante la rimozione');
        } finally {
            setLoading(false);
        }
    };

    const aggiungiFascia = () => {
        setFormData({
            ...formData,
            scheduleItems: [...formData.scheduleItems, { id: Date.now().toString(), dayOfWeek: '', startTime: '', endTime: '', location: '' }]
        });
    };

    const aggiornaFascia = (index, campo, valore) => {
        setFormData({
            ...formData,
            scheduleItems: formData.scheduleItems.map((f, i) => (i === index ? { ...f, [campo]: valore } : f))
        });
    };

    const rimuoviFascia = async (index) => {
        if (!(await chiediConferma({ titolo: 'Rimuovere la fascia?', testo: 'La fascia oraria sparisce dalla disponibilità.' }))) return;
        setFormData({ ...formData, scheduleItems: formData.scheduleItems.filter((_, i) => i !== index) });
    };

    const getSportLabel = (sportValue) => {
        const sport = enabledSports.find(s => s.value === sportValue) || sports.find(s => s.value === sportValue);
        return sport ? sport.label : sportValue;
    };

    // Orari della riga: un giorno per riga con le sue fasce
    const righeOrari = (items = []) => dayOptions
        .map((day) => {
            const fasce = items
                .filter((h) => h.dayOfWeek === day.value)
                .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
                .map((h) => `${h.startTime}–${h.endTime}${h.location ? ` (${h.location})` : ''}`);
            return fasce.length ? { giorno: day.label, fasce: fasce.join(', ') } : null;
        })
        .filter(Boolean);

    // Riga sotto gli orari impegnati: quante sedute e i prodotti da cui spostarle (collegamenti diretti)
    const collegamentiSedute = (eventi) => {
        const prodotti = [];
        const perChiave = {};
        let inAttesa = false;
        eventi.forEach((e) => {
            const p = risolti[chiaveGruppo(e)];
            if (!p) {
                inAttesa = true;
                return;
            }
            const k = `${p.tipo}|${p.id || chiaveGruppo(e)}`;
            if (!perChiave[k]) {
                perChiave[k] = { ...p, chiave: k, sedute: 0 };
                prodotti.push(perChiave[k]);
            }
            perChiave[k].sedute += 1;
        });
        const n = eventi.length;
        return (
            <div className="sid-disp-fascia__info">
                <span>{n} {n === 1 ? 'seduta prenotata' : 'sedute prenotate'} — prima spostale nei prodotti:</span>
                {prodotti.map((p) => (p.url ? (
                    <Button
                        key={p.chiave}
                        size="sm"
                        variant="outline-dark"
                        className="sid-btn-compact"
                        title={`${TIPI_PRODOTTO[p.tipo] || 'Evento'} · ${p.sedute} ${p.sedute === 1 ? 'seduta' : 'sedute'}`}
                        onClick={() => navigate(p.url)}
                    >
                        {p.tipo === 'corso' ? 'Vai al corso' : p.tipo === 'lezione' ? 'Vai alla lezione' : 'Vai alla prenotazione'}: {p.nome}
                    </Button>
                ) : (
                    <span key={p.chiave} className="sid-disp-fascia__prodotto">{p.nome}</span>
                )))}
                {inAttesa && <span className="text-muted">ricerca prodotti…</span>}
            </div>
        );
    };

    const esitoVerifica = () => {
        if (!verificaAttiva) return null;
        if (sedute.inCorso) return <span className="sid-check sid-check--wait">Verifica sedute in corso…</span>;
        if (sedute.errore) return <span className="sid-check sid-check--wait">{sedute.errore}</span>;
        if (scoperte.length === 0) return null;
        const elenco = scoperte.slice(0, 3).map(descriviSeduta).join('; ');
        return (
            <span className="sid-check sid-check--ko">
                {scoperte.length} {scoperte.length === 1 ? 'seduta prenotata resterebbe fuori' : 'sedute prenotate resterebbero fuori'}: {elenco}
                {scoperte.length > 3 ? ' …' : ''} Spostale prima nei prodotti (corsi, lezioni).
            </span>
        );
    };

    // Il modulo, aperto nella pagina sotto l'elenco (come la pianificazione di una squadra)
    const modulo = () => {
        const item = editingItem;
        const modifica = modificabile(item);
        const bloccato = !modifica;
        const attiva = !!item && item.status === 'ACTIVE';
        const verificaKo = verificaAttiva && !sedute.inCorso && !sedute.errore && scoperte.length > 0;
        const bloccaCancella = attiva && (sedute.inCorso || sedute.lista.length > 0);
        const esito = esitoVerifica();
        const bloccateDi = (f) => seduteFascia(f);
        return (
            <>
                <h5 className="sid-form-section">{item ? 'Modifica disponibilità' : 'Nuova disponibilità'}</h5>
                <Row className="g-4">
                    <Col md={4}>
                        <Form.Label htmlFor="disp-sport">Sport *</Form.Label>
                        <div className="mt-2">
                            {enabledSports.length > 0 ? (
                                <Select
                                    inputId="disp-sport"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    placeholder="Seleziona sport"
                                    isDisabled={bloccato}
                                    value={enabledSports.find(option => option.value === formData.sport) || null}
                                    onChange={(selectedOption) => setFormData({ ...formData, sport: selectedOption?.value || '' })}
                                    options={enabledSports}
                                />
                            ) : (
                                <div className="text-muted">Caricamento sport...</div>
                            )}
                        </div>
                    </Col>
                    <Col md={4}>
                        <Form.Label htmlFor="disp-data-inizio">Data inizio *</Form.Label>
                        <Form.Control
                            id="disp-data-inizio"
                            className="mt-2"
                            type="date"
                            value={formData.startDate}
                            disabled={bloccato}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        />
                    </Col>
                    <Col md={4}>
                        <Form.Label htmlFor="disp-data-fine">Data fine *</Form.Label>
                        <Form.Control
                            id="disp-data-fine"
                            className="mt-2"
                            type="date"
                            value={formData.endDate}
                            disabled={bloccato}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        />
                    </Col>
                </Row>

                <h5 className="sid-form-section">Orari settimanali</h5>

                {formData.scheduleItems.length > 0 && (
                    <Row className="g-2 sid-disp-fasce__head">
                        <Col xs={12} md={3}><Form.Label className="m-0">Giorno *</Form.Label></Col>
                        <Col xs={6} md={2}><Form.Label className="m-0">Inizio *</Form.Label></Col>
                        <Col xs={6} md={2}><Form.Label className="m-0">Fine *</Form.Label></Col>
                        <Col xs={12} md={4}><Form.Label className="m-0">Luogo (facoltativo)</Form.Label></Col>
                    </Row>
                )}
                {formData.scheduleItems.map((fascia, index) => {
                    const impegnate = bloccateDi(fascia);
                    const bloccata = bloccato || impegnate.length > 0;
                    return (
                        <div key={fascia.id || index} className={`sid-disp-fascia${impegnate.length > 0 ? ' is-locked' : ''}`}>
                            <Row className="g-2 align-items-center">
                                <Col xs={12} md={3}>
                                    <Select
                                        inputId={`disp-giorno-${index}`}
                                        aria-label="Giorno"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        placeholder=""
                                        isDisabled={bloccata}
                                        value={dayOptions.find(option => option.value === fascia.dayOfWeek) || null}
                                        onChange={(o) => aggiornaFascia(index, 'dayOfWeek', o?.value || '')}
                                        options={dayOptions}
                                    />
                                </Col>
                                <Col xs={6} md={2}>
                                    <Form.Control
                                        id={`disp-ora-inizio-${index}`}
                                        aria-label="Ora inizio"
                                        type="time"
                                        disabled={bloccata}
                                        value={fascia.startTime || ''}
                                        onChange={(e) => aggiornaFascia(index, 'startTime', e.target.value)}
                                    />
                                </Col>
                                <Col xs={6} md={2}>
                                    <Form.Control
                                        id={`disp-ora-fine-${index}`}
                                        aria-label="Ora fine"
                                        type="time"
                                        disabled={bloccata}
                                        value={fascia.endTime || ''}
                                        onChange={(e) => aggiornaFascia(index, 'endTime', e.target.value)}
                                    />
                                </Col>
                                <Col xs={10} md={4}>
                                    <Form.Control
                                        aria-label="Luogo (facoltativo)"
                                        type="text"
                                        disabled={bloccata}
                                        value={fascia.location || ''}
                                        onChange={(e) => aggiornaFascia(index, 'location', e.target.value)}
                                    />
                                </Col>
                                <Col xs={2} md={1}>
                                    {!bloccata && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="sid-btn-compact"
                                            aria-label="Rimuovi orario"
                                            onClick={() => rimuoviFascia(index)}
                                        >
                                            <Trash size="14" />
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                            {impegnate.length > 0 && collegamentiSedute(impegnate)}
                        </div>
                    );
                })}

                {modifica && (
                    <div className="mt-3">
                        <Button id="disp-aggiungi-orario" variant="outline-primary" size="sm" className="sid-btn-compact" onClick={aggiungiFascia}>
                            <Add size="14" className="me-1" />
                            Aggiungi orario
                        </Button>
                    </div>
                )}

                {esito && <div className="mt-4">{esito}</div>}
                {errorMessage && <div className="sid-check sid-check--ko mt-4">{errorMessage}</div>}

                {/* Tasti in fondo, come in Squadra: l'azione che toglie a sinistra, Annulla e Salva a destra (la regola SUI li centra) */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-4 mt-4">
                    <div className="d-flex flex-wrap gap-2">
                        {item && (
                            <span
                                className="d-inline-block"
                                title={bloccaCancella ? 'Ci sono sedute prenotate in questo periodo: spostale o annullale prima nei prodotti' : undefined}
                            >
                                <Button variant="outline-danger" size="sm" disabled={loading || bloccaCancella} onClick={() => rimuovi(item)}>
                                    Elimina disponibilità
                                </Button>
                            </span>
                        )}
                    </div>
                    <div className="d-flex flex-wrap gap-4">
                        <button type="button" className="btn btn-dark light" onClick={chiudi} disabled={loading}>Annulla</button>
                        {modifica && attiva && (
                            <Button className="btn btn-primary" onClick={() => salva(false)} disabled={loading || sedute.inCorso || verificaKo}>
                                {loading ? 'Salvataggio…' : 'Salva'}
                            </Button>
                        )}
                        {modifica && !attiva && (
                            <>
                                <Button className="btn btn-outline-primary" variant="outline-primary" onClick={() => salva(false)} disabled={loading}>
                                    {loading ? 'Salvataggio…' : 'Salva come bozza'}
                                </Button>
                                <Button className="btn btn-primary" onClick={() => salva(true)} disabled={loading}>
                                    Salva e attiva
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    };

    if (loading && disponibilita.length === 0) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="sr-only">Caricamento...</span>
                </Spinner>
                <div>Caricamento disponibilità...</div>
            </div>
        );
    }

    const filteredDisponibilita = disponibilita.filter(item => item.status !== 'CANCELLED');
    const moduloAperto = !readonly && (nuova || !!editingItem);

    return (
        <Card>
            <Card.Body>
                {filteredDisponibilita.length === 0 ? (!moduloAperto && (
                    <div className="text-center">
                        <Calendar size="64" className="text-muted mb-4" />
                        <h6 className="text-muted mb-3">Nessuna disponibilità configurata</h6>
                        {!readonly && <p className="text-muted mb-0">Crea la prima disponibilità con il tasto qui sotto.</p>}
                    </div>
                )) : (
                    <div className="table-responsive">
                        <table className={`sid-plan-table${readonly ? ' sid-plan-table--static' : ''}`}>
                            <tbody>
                                {filteredDisponibilita.map((item) => {
                                    const aperta = !readonly && editingItem?.id === item.id;
                                    const stato = statusLabels[item.status] || { label: item.status, cls: 'text-muted' };
                                    const orari = righeOrari(item.scheduleItems);
                                    const interazione = readonly ? {} : {
                                        role: 'button',
                                        tabIndex: 0,
                                        onClick: () => apriPannello(item),
                                        onKeyDown: (e) => { if (e.key === 'Enter') apriPannello(item); }
                                    };
                                    return (
                                        <tr key={item.id} className={aperta ? 'is-open' : undefined} {...interazione}>
                                            <td>{getSportLabel(item.sport)}</td>
                                            <td className="text-nowrap">{dataIt(item.startDate)} – {dataIt(item.endDate)}</td>
                                            <td>
                                                {orari.length > 0 ? orari.map((r) => (
                                                    <div key={r.giorno} className="sid-disp-orario">
                                                        <span>{r.giorno}</span> {r.fasce}
                                                    </div>
                                                )) : <span className="text-muted">Nessun orario configurato</span>}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <i className={`fa fa-circle me-1 ${stato.cls}`} />{stato.label}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Un solo tasto, al centro, sotto il testo o l'elenco; sparisce mentre il modulo è aperto */}
                {!readonly && !moduloAperto && (
                    <div className="d-flex justify-content-center mt-4">
                        <Button variant="primary" size="sm" className="btn-add" onClick={apriNuova} disabled={loading}>
                            <Add size="16" className="me-1" />
                            Nuova Disponibilità
                        </Button>
                    </div>
                )}

                {moduloAperto && modulo()}
            </Card.Body>
        </Card>
    );
};

export default DisponibilitaMaestro;
